import { Box, TableRow, Typography } from '@mui/material';
import { mdiCheckboxBlankOff } from '@mdi/js';
import { useTranslation } from 'react-i18next';

import { Checkbox } from 'common/components/Checkbox';
import { Chip, ChipGroup } from 'common/components/Chip';
import { TableCell } from 'common/components/Table';
import {
    ICertificateWithScoringFragment,
    IOfferEventDate,
    IOfferEventEnrollmentManageableFragment,
    IOfferEventSubscriptionStatus,
    IOfferEventType,
} from 'graphql/types';
import { OfferEventSubscriptionStatusChip } from 'offer/components/OfferEventSubscriptionStatusChip';
import { Icon } from 'common/components/Icon';
import { UserAvatar } from 'user/components/UserAvatar';
import { EnrollmentsTableActions } from 'offer/components/EnrollmentsTableActions';
import { TOfferEventDetailEnrollment } from 'offer/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { OfferEventAttendanceChips } from 'offer/components/OfferEventAttendanceChips';

interface IProps {
    certificate?: ICertificateWithScoringFragment;
    disabled?: boolean;
    disabledRowClick?: boolean;
    hasForGroup?: boolean;
    isEditable?: boolean;
    isSelectable?: boolean;
    isSelected?: boolean;
    offerEventDates: IOfferEventDate[];
    subscription: TOfferEventDetailEnrollment;
    offerEventType: IOfferEventType;
    onCreateCertificates?(): void;
    onMailDialog?(): void;
    onSelect(): void;
    onSubscribeDeleteDialog(
        open: boolean,
        selectedAmount: number,
        userId?: string
    ): void;
    onSubscribeStatusDialog(
        open: boolean,
        selectedAmount: number,
        status?: IOfferEventSubscriptionStatus,
        userId?: string
    ): void;
    onAttendanceDialog(open: boolean, enrollmentIds?: string[]): void;
}

export const SubscriptionRow = ({
    certificate,
    disabled,
    disabledRowClick,
    hasForGroup,
    isEditable = false,
    isSelectable = false,
    isSelected,
    offerEventDates,
    subscription,
    offerEventType,
    onCreateCertificates,
    onMailDialog,
    onSelect,
    onSubscribeDeleteDialog,
    onSubscribeStatusDialog,
    onAttendanceDialog,
}: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer } = useGlobalDrawer();
    const { id: subscriptionId, user, forGroup } = subscription;

    let status,
        waitingListNumber,
        hasCertificate,
        portfolioItem,
        attendance: IOfferEventEnrollmentManageableFragment['attendance'] = [],
        reason;

    if (subscription.__typename === 'OfferEventEnrollmentManageable') {
        status = subscription.status;
        waitingListNumber = subscription.waitingListNumber;
        hasCertificate = subscription.hasCertificate;
        portfolioItem = subscription.portfolioItem;
        attendance = subscription.attendance || [];
        reason = subscription.reason;
    }

    if (!user) return null;

    const { id, name } = user;

    const isWebinarOrMeeting = [
        IOfferEventType.Webinar,
        IOfferEventType.Meeting,
    ].includes(offerEventType);

    const isDisabled = !isEditable || disabled;
    const currentUserOnWaitingList =
        status === IOfferEventSubscriptionStatus.Full && !!waitingListNumber;

    return (
        <TableRow hover key={subscriptionId}>
            {isSelectable && (
                <TableCell>
                    {isEditable ? (
                        <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onClick={(e) => {
                                onSelect?.();

                                e.stopPropagation();
                            }}
                        />
                    ) : (
                        <Checkbox
                            disabled
                            checked={false}
                            icon={
                                <Icon
                                    path={mdiCheckboxBlankOff}
                                    size="2.4rem"
                                />
                            }
                        />
                    )}
                </TableCell>
            )}
            <TableCell
                sx={{ cursor: !disabledRowClick ? 'pointer' : 'initial' }}
                onClick={() => {
                    if (disabledRowClick) return;

                    openGlobalDrawer({
                        type: IGlobalDrawerType.UserProfile,
                        itemId: id,
                    });
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <UserAvatar bgColor="secondary" size={45} user={user} />

                    <Box sx={{ ml: 2 }}>
                        <Typography variant="body2">{name}</Typography>
                    </Box>
                </Box>
            </TableCell>

            {!isSelectable && !!hasForGroup && (
                <TableCell>
                    {!!forGroup && (
                        <ChipGroup sx={{ justifyContent: 'flex-end' }}>
                            <Chip label={forGroup.name} />
                        </ChipGroup>
                    )}
                </TableCell>
            )}

            {isSelectable && (
                <TableCell>
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: !isSelectable ? 'right' : 'left',
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                ml: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    mr: 2,
                                }}
                            >
                                <ChipGroup sx={{ justifyContent: 'flex-end' }}>
                                    {!!forGroup && (
                                        <Chip label={forGroup.name} />
                                    )}

                                    {hasCertificate && (
                                        <Chip
                                            bgColor="default"
                                            label={translate(
                                                'enrollmentsTable.labelCertificate'
                                            )}
                                        />
                                    )}

                                    <OfferEventAttendanceChips
                                        offerEventDates={offerEventDates}
                                        userAttendance={attendance}
                                    />

                                    {!!status && (
                                        <OfferEventSubscriptionStatusChip
                                            reason={reason || undefined}
                                            subscriptionStatus={status}
                                        />
                                    )}

                                    {currentUserOnWaitingList && (
                                        <Chip
                                            bgColor="warning"
                                            label={translate(
                                                'waitingListSubscriptionLabelCount',
                                                { waitingListNumber }
                                            )}
                                        />
                                    )}
                                </ChipGroup>

                                {!!portfolioItem && (
                                    <PortfolioItemProgressScoreChip
                                        hover
                                        portfolioItem={portfolioItem}
                                        sx={{
                                            cursor: 'pointer',
                                            ml: 2,
                                        }}
                                        onClick={() => {
                                            openGlobalDrawer({
                                                type: IGlobalDrawerType.PortfolioItem,
                                                itemId: portfolioItem.id,
                                                props: {
                                                    hideOfferEventListItem: true,
                                                },
                                            });
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <EnrollmentsTableActions
                            enablePortal
                            isWebinarOrMeeting={isWebinarOrMeeting}
                            zIndex={1250}
                            onClickCertificate={
                                !!certificate ? onCreateCertificates : undefined
                            }
                            onClickComplete={() => {
                                onAttendanceDialog(true, [subscriptionId]);
                            }}
                            onClickDelete={() => {
                                onSubscribeDeleteDialog(
                                    true,
                                    1,
                                    subscriptionId
                                );
                            }}
                            onClickEnroll={() => {
                                onSubscribeStatusDialog(
                                    true,
                                    1,
                                    IOfferEventSubscriptionStatus.Enrolled,
                                    subscriptionId
                                );
                            }}
                            onClickMail={onMailDialog}
                            onClickReject={() => {
                                onSubscribeStatusDialog(
                                    true,
                                    1,
                                    IOfferEventSubscriptionStatus.Denied,
                                    subscriptionId
                                );
                            }}
                        />
                    </Box>
                </TableCell>
            )}
        </TableRow>
    );
};
